import { useState } from 'react'
import { FiAlertCircle, FiX, FiEdit, FiCalendar, FiCheckSquare, FiSquare } from 'react-icons/fi'
import SolicitudCorreccionModal from './SolicitudCorreccionModal'
import SolicitudAusenciaModal from './SolicitudAusenciaModal'
import { Btn, Pill } from '../../ui'
import { formatearFecha } from '../../utils/fechas'

/*
 * ModalDiasSinJustificar (V2) — popup de inicio con lenguaje DS v2.
 * Misma lógica que el legacy: lista de días, multi-select, acciones grupales.
 */

const calcularDiasTranscurridos = (fecha) => {
  const hoy = new Date()
  hoy.setHours(0, 0, 0, 0)
  const fechaDia = new Date(fecha + 'T12:00:00')
  fechaDia.setHours(0, 0, 0, 0)
  return Math.floor((hoy - fechaDia) / (1000 * 60 * 60 * 24))
}

const ModalDiasSinJustificarV2 = ({ dias, onClose, onJustificado }) => {
  const [seleccionados, setSeleccionados] = useState([])
  const [modalCorreccion, setModalCorreccion] = useState(null)
  const [modalAusencia, setModalAusencia] = useState(null)

  const toggleSeleccion = (diaId) =>
    setSeleccionados(prev => prev.includes(diaId) ? prev.filter(id => id !== diaId) : [...prev, diaId])

  const toggleTodos = () => {
    if (seleccionados.length === dias.length) setSeleccionados([])
    else setSeleccionados(dias.map(d => d.id))
  }

  const diasSeleccionados = dias.filter(d => seleccionados.includes(d.id))
  const haySeleccion = seleccionados.length > 0

  const handleCorreccionGrupal = () => {
    const fechas = diasSeleccionados.map(d => d.fecha).sort()
    setModalCorreccion({ fechas })
  }

  const handleAusenciaGrupal = () => {
    const fechas = diasSeleccionados.map(d => d.fecha).sort()
    setModalAusencia({ fechaInicio: fechas[0], fechaFin: fechas[fechas.length - 1] })
  }

  const handleCorreccionSuccess = () => {
    setModalCorreccion(null)
    setSeleccionados([])
    if (onJustificado) onJustificado()
  }

  const handleAusenciaSuccess = () => {
    setModalAusencia(null)
    setSeleccionados([])
    if (onJustificado) onJustificado()
  }

  if (modalCorreccion) {
    if (modalCorreccion.fechas && modalCorreccion.fechas.length === 1) {
      return (
        <SolicitudCorreccionModal
          fichaje={null}
          fechaSugerida={modalCorreccion.fechas[0]}
          onClose={() => setModalCorreccion(null)}
          onSuccess={handleCorreccionSuccess}
        />
      )
    }
    return (
      <SolicitudCorreccionModal
        fichaje={null}
        fechaSugerida={modalCorreccion.fechas[0]}
        fechasMultiples={modalCorreccion.fechas}
        onClose={() => setModalCorreccion(null)}
        onSuccess={handleCorreccionSuccess}
      />
    )
  }

  if (modalAusencia) {
    return (
      <SolicitudAusenciaModal
        fechaSugerida={modalAusencia.fechaInicio}
        fechaSugeridaFin={modalAusencia.fechaFin}
        onClose={() => setModalAusencia(null)}
        onSuccess={handleAusenciaSuccess}
      />
    )
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="ui-card w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start gap-3 p-5 border-b border-[var(--line)]">
          <div className="p-2 rounded-soft bg-[rgba(245,158,11,.10)]">
            <FiAlertCircle size={18} className="text-[#A86A06]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow mb-1">Días sin justificar</div>
            <h2 className="font-display text-[22px] text-[var(--ink)] leading-tight">
              {dias.length} {dias.length === 1 ? 'día pendiente' : 'días pendientes'}.
            </h2>
            <p className="text-[12.5px] text-[var(--ink-3)] mt-1">
              Selecciona días para justificar en grupo.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-soft text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-2)] transition-colors"
            aria-label="Cerrar"
          >
            <FiX size={16} />
          </button>
        </div>

        {/* Barra de selección grupal */}
        <div className="flex items-center justify-between px-5 py-2.5 bg-[var(--bg-2)] border-b border-[var(--line)]">
          <button
            type="button"
            onClick={toggleTodos}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors"
          >
            {seleccionados.length === dias.length
              ? <FiCheckSquare size={13} className="text-[var(--brand-1)]" />
              : <FiSquare size={13} />}
            {seleccionados.length === dias.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
          </button>
          {haySeleccion && (
            <Pill tone="brand">
              {seleccionados.length} seleccionado{seleccionados.length > 1 ? 's' : ''}
            </Pill>
          )}
        </div>

        {/* Lista compacta */}
        <div className="overflow-y-auto flex-1 p-2">
          {dias.map(dia => {
            const diasT = calcularDiasTranscurridos(dia.fecha)
            const isSelected = seleccionados.includes(dia.id)
            return (
              <div
                key={dia.id}
                className={`flex items-center justify-between px-3 py-2.5 rounded-soft cursor-pointer transition-colors ${
                  isSelected ? 'bg-[rgba(37,99,235,.06)]' : 'hover:bg-[var(--bg-2)]'
                }`}
                onClick={() => toggleSeleccion(dia.id)}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  {isSelected
                    ? <FiCheckSquare size={14} className="text-[var(--brand-1)] shrink-0" />
                    : <FiSquare size={14} className="text-[var(--ink-3)] shrink-0" />}
                  <span className="text-[13px] font-medium text-[var(--ink)] capitalize truncate font-mono tnum">
                    {formatearFecha(dia.fecha)}
                  </span>
                  <span className="text-[11px] text-[var(--ink-3)] shrink-0">
                    hace {diasT}d
                  </span>
                </div>
                {!haySeleccion && (
                  <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => setModalCorreccion({ fechas: [dia.fecha] })}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-soft text-[var(--ink-3)] hover:text-[var(--brand-1)] hover:bg-[rgba(37,99,235,.06)] transition-colors"
                      title="Solicitar corrección"
                    >
                      <FiEdit size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setModalAusencia({ fechaInicio: dia.fecha, fechaFin: dia.fecha })}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-soft text-[var(--ink-3)] hover:text-[#7C3AED] hover:bg-[rgba(124,58,237,.06)] transition-colors"
                      title="Declarar ausencia"
                    >
                      <FiCalendar size={13} />
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-[var(--line)]">
          {haySeleccion ? (
            <div className="flex gap-2">
              <Btn variant="primary" size="sm" className="flex-1" onClick={handleCorreccionGrupal}>
                <FiEdit size={13} /> Corrección ({seleccionados.length})
              </Btn>
              <Btn variant="ghost" size="sm" className="flex-1" onClick={handleAusenciaGrupal}>
                <FiCalendar size={13} /> Ausencia ({seleccionados.length})
              </Btn>
            </div>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="w-full px-3 py-2 text-[12.5px] text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-2)] rounded-soft transition-colors"
            >
              Recordar más tarde
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ModalDiasSinJustificarV2
